import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { api } from './api';
import { useMultiHOA } from './MultiHOAContext';
import HOATrack from './components/HOATrack';
import AmenityBookingCalendar from './components/AmenityBookingCalendar';
import DynamicTimeSlotPicker from './components/DynamicTimeSlotPicker';
import DynamicAmenityDropdown from './components/DynamicAmenityDropdown';

// Form Modal Component
const FormModal = ({ template, onClose, onSubmit }) => {
  const { user } = useContext(AuthContext);
  const { activeHoaId } = useMultiHOA();
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showBookingCalendar, setShowBookingCalendar] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState('');
  const [selectedAmenityDetails, setSelectedAmenityDetails] = useState(null);
  
  // HOA Track settings for controlling assignment options
  const [hoaTrackSettings, setHoaTrackSettings] = useState({
    committees_enabled: true,
    security_enabled: true,
    maintenance_enabled: true
  });

  const handleFieldChange = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const currentHoaId = activeHoaId || user?.hoa_id;
        const response = await api.post('/api/settings/upload-file', formData, {
          params: { hoa_id: currentHoaId },
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return {
          file_id: response.data.file_id,
          filename: response.data.filename,
          file_size: response.data.file_size
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setUploadedFiles(prev => [...prev, ...uploadedFiles]);
    } catch (error) {
      console.error('File upload failed:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeFile = (fileIndex) => {
    setUploadedFiles(prev => prev.filter((_, index) => index !== fileIndex));
  };

  const handleBookingSelection = (bookingData) => {
    // Persist booking details into answers for backend processing and UI display
    handleFieldChange('reservation_booking', bookingData);
    if (bookingData?.date) {
      handleFieldChange('date', bookingData.date);
      handleFieldChange('reservation_date', bookingData.date);
    }
    if (bookingData?.start_time) handleFieldChange('start_time', bookingData.start_time);
    if (bookingData?.end_time) handleFieldChange('end_time', bookingData.end_time);
    setShowBookingCalendar(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submissionData = {
        ...answers,
        attachments: uploadedFiles
      };
      await onSubmit(template.key, submissionData);
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const { key, label, type, required, options } = field;
    const value = answers[key] || '';

    switch (type) {
      case 'dynamic_amenity_select':
        // Explicit support for dynamic amenity dropdown field type
        return (
          <div key={key}>
            <DynamicAmenityDropdown
              value={value}
              onChange={(selectedValue) => handleFieldChange(key, selectedValue)}
              required={required}
              onAmenitySelect={(amenityDetails) => {
                setSelectedAmenityDetails(amenityDetails);
              }}
            />
            {selectedAmenityDetails && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-blue-900">{selectedAmenityDetails.name}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {selectedAmenityDetails.block_minutes}min slots
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      📅 {selectedAmenityDetails.advance_booking_days || 14}d advance
                    </span>
                  </div>
                  {selectedAmenityDetails.description && (
                    <p className="text-blue-800 mb-2">{selectedAmenityDetails.description}</p>
                  )}
                  <div className="text-xs text-blue-600 space-y-1">
                    <div>⏰ Hours: {selectedAmenityDetails.hours_start} - {selectedAmenityDetails.hours_end}</div>
                    <div>👥 Capacity: {selectedAmenityDetails.capacity} {selectedAmenityDetails.capacity === 1 ? 'reservation' : 'concurrent reservations'}</div>
                    {selectedAmenityDetails.rules && selectedAmenityDetails.rules.length > 0 && (
                      <div>
                        <span className="font-medium">Rules:</span>
                        <ul className="list-disc list-inside ml-2">
                          {selectedAmenityDetails.rules.map((rule, index) => (
                            <li key={index}>{rule}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Inject DATE field directly after amenity selection to match required layout */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={answers.reservation_date || ''}
                min={new Date().toISOString().split('T')[0]}
                max={(() => { const d = new Date(); d.setDate(d.getDate() + (selectedAmenityDetails?.advance_booking_days ?? 14)); return d.toISOString().split('T')[0]; })()}
                onChange={(e) => handleFieldChange('reservation_date', e.target.value)}
              />
            </div>
          </div>
        );

      case 'select':
        // Use dynamic amenity dropdown when amenity field is modeled as a select
        if ((template.key === 'amenity_reservation' || template.name?.toLowerCase().includes('amenity')) && 
            (key === 'amenity' || key === 'amenity_type')) {
          return (
            <div key={key}>
              <DynamicAmenityDropdown
                value={value}
                onChange={(selectedValue) => handleFieldChange(key, selectedValue)}
                required={required}
                onAmenitySelect={(amenityDetails) => {
                  setSelectedAmenityDetails(amenityDetails);
                }}
              />
              {selectedAmenityDetails && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="text-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-blue-900">{selectedAmenityDetails.name}</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {selectedAmenityDetails.block_minutes}min slots
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        📅 {selectedAmenityDetails.advance_booking_days || 14}d advance
                      </span>
                    </div>
                    {selectedAmenityDetails.description && (
                      <p className="text-blue-800 mb-2">{selectedAmenityDetails.description}</p>
                    )}
                    <div className="text-xs text-blue-600 space-y-1">
                      <div>⏰ Hours: {selectedAmenityDetails.hours_start} - {selectedAmenityDetails.hours_end}</div>
                      <div>👥 Capacity: {selectedAmenityDetails.capacity} {selectedAmenityDetails.capacity === 1 ? 'reservation' : 'concurrent reservations'}</div>
                      {selectedAmenityDetails.rules && selectedAmenityDetails.rules.length > 0 && (
                        <div>
                          <span className="font-medium">Rules:</span>
                          <ul className="list-disc list-inside ml-2">
                            {selectedAmenityDetails.rules.map((rule, index) => (
                              <li key={index}>{rule}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }
        
        return (
          <select
            key={key}
            required={required}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          >
            <option value="">Select {label}</option>
            {options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            key={key}
            required={required}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );
      
      case 'checkbox':
        return (
          <label key={key} className="flex items-center">
            <input
              type="checkbox"
              className="mr-2"
              checked={value}
              onChange={(e) => handleFieldChange(key, e.target.checked)}
            />
            <span>{label}</span>
          </label>
        );
      
      case 'date':
        return (
          <input
            key={key}
            type="date"
            required={required}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          />
        );
      
      case 'time':
        return (
          <input
            key={key}
            type="time"
            required={required}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          />
        );
      
      case 'number':
        return (
          <input
            key={key}
            type="number"
            required={required}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );
      
      case 'tel':
        return (
          <input
            key={key}
            type="tel"
            required={required}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );
      
      case 'booking_calendar':
        // Hide legacy booking button; we render inline date + slots below for amenity reservations
        return null;
      
      default:
        return (
          <input
            key={key}
            type="text"
            required={required}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">{template.name}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          
          {template.description && (
            <p className="text-gray-600 mb-6">{template.description}</p>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {template.fields.map(field => {
              // Skip time fields for amenity reservations - we'll use DynamicTimeSlotPicker below the form
              if ((template.key === 'amenity_reservation' || template.name?.toLowerCase().includes('amenity')) && 
                  (field.key === 'start_time' || field.key === 'end_time' || 
                   field.key === 'preferred_start_time' || field.key === 'preferred_end_time')) {
                return null;
              }
              
              return (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                  {/* Special note for property_address field in ARC forms */}
                  {template.key === 'arc' && field.key === 'property_address' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Enter property address if different from your account address
                    </p>
                  )}
                </div>
              );
            })}
            

            
            {/* Dynamic Time Slot Picker for Amenity Reservations */}
            {(template.key === 'amenity_reservation' || template.name?.toLowerCase().includes('amenity')) && 
             answers.amenity && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-md font-medium text-gray-900">🕐 Select Time Slot</h4>
                  {hoaTrackSettings?.amenity_daily_limit_per_user && (
                    <span className="text-xs text-gray-500">Limit: {hoaTrackSettings.amenity_daily_limit_per_user} reservations per day</span>
                  )}
                </div>
                {answers.reservation_date ? (
                  <DynamicTimeSlotPicker
                    amenityType={answers.amenity}
                    selectedDate={answers.reservation_date}
                    selectedStartTime={answers.start_time}
                    selectedEndTime={answers.end_time}
                    onSlotSelect={(slotData) => {
                      handleFieldChange('start_time', slotData.start_time);
                      handleFieldChange('end_time', slotData.end_time);
                      handleFieldChange('duration_minutes', slotData.duration_minutes);
                    }}
                  />
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-3">
                    Select a date above to see available time slots.
                  </div>
                )}
              </div>
            )}
            
            {/* Show message to select amenity and date first for amenity reservations */}
            {(template.key === 'amenity_reservation' || template.name?.toLowerCase().includes('amenity')) && 
             (!answers.amenity || !answers.reservation_date) && (
              <div className="border-t pt-4">
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <div className="text-4xl mb-2">🏊‍♂️</div>
                  <p className="font-medium">Select Amenity Type and Date</p>
                  <p className="text-sm mt-1">Time slots will appear once you select an amenity and date above</p>
                </div>
              </div>
            )}
            
            {/* ARC-Specific: After the Fact Checkbox */}
            {template.key === 'arc' && (
              <div className="pt-4 border-t">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={answers.after_the_fact || false}
                    onChange={(e) => handleFieldChange('after_the_fact', e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">After the Fact Request</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Check this if you are submitting a request for work that was already completed 
                      (e.g., by a previous owner) and you need protective approval.
                    </p>
                  </div>
                </label>
              </div>
            )}
            
            {/* ARC-Specific: Important Notice */}
            {template.key === 'arc' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <h4 className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <span>⚠️</span> Important Notice
                </h4>
                <p className="text-sm text-amber-700 mt-2">
                  ARC requests require community notices to be sent out before a meeting is held. Please factor time for the committee to review request, follow up, and schedule a meeting. Requests may be started <strong>only after approved</strong> by the ARC committee and/or state law requirements.
                </p>
              </div>
            )}
            
            {/* File Upload Section */}
            <div className="pt-4 border-t">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supporting Documents {template.key === 'arc' ? <span className="text-red-500">*</span> : '(Optional)'}
              </label>
              {template.key === 'arc' && (
                <p className="text-xs text-amber-600 mb-2">
                  📋 For fences and structural changes, please include material type in your description and upload relevant documents.
                </p>
              )}
              <div className="space-y-3">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt"
                  onChange={handleFileUpload}
                  disabled={uploadingFiles}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                
                {uploadingFiles && (
                  <div className="text-sm text-blue-600">Uploading files...</div>
                )}
                
                {uploadedFiles.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Uploaded Files:</div>
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{file.filename}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Validation message for ARC */}
                {template.key === 'arc' && uploadedFiles.length === 0 && (
                  <div className="text-xs text-red-500">
                    At least one document is required for ARC requests.
                  </div>
                )}
                
                <div className="text-xs text-gray-500">
                  Supported formats: PDF, Images (JPG, PNG, GIF), Word documents, Text files. Max 10MB per file.
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || uploadingFiles || (template.key === 'arc' && uploadedFiles.length === 0)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Comments Section Component
const CommentsSection = ({ submission, user, activeHoaId, isBoardMember, isCommitteeMember, onContentAdded }) => {
  const [comments, setComments] = useState(submission.comments || []);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRefreshNote, setShowRefreshNote] = useState(false);
  
  const isOriginator = submission.created_by === user?.id;
  const canComment = isOriginator || isBoardMember || isCommitteeMember;
  
  const handleAddComment = async () => {
    if (!newComment.trim() || !canComment) return;
    
    setLoading(true);
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      const response = await api.post(`/api/forms/submissions/${submission.id}/comments`, {
        text: newComment
      }, {
        params: { hoa_id: currentHoaId }
      });
      
      if (response.data.success) {
        setComments([...comments, response.data.comment]);
        setNewComment('');
        // Show refresh note for originator
        if (isOriginator) {
          setShowRefreshNote(true);
          if (onContentAdded) onContentAdded();
        }
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      alert('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="font-medium mb-3">💬 Comments</h3>
      
      {/* Existing Comments */}
      <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
        {comments.length === 0 ? (
          <p className="text-sm text-gray-500">No comments yet.</p>
        ) : (
          comments.map((comment, index) => (
            <div key={comment.id || index} className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between items-start mb-1">
                <span className="text-sm font-medium">{comment.added_by_name || 'Unknown'}</span>
                <span className="text-xs text-gray-500">
                  {comment.added_at ? new Date(comment.added_at).toLocaleString() : ''}
                </span>
              </div>
              <p className="text-sm text-gray-700">{comment.text}</p>
            </div>
          ))
        )}
      </div>
      
      {/* Add Comment */}
      {canComment && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <button
            onClick={handleAddComment}
            disabled={loading || !newComment.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            {loading ? '...' : 'Send'}
          </button>
        </div>
      )}
      
      {/* Refresh note for originator after adding content */}
      {showRefreshNote && isOriginator && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            ✓ Comment added successfully. Refresh the page to see updates and use the "Alert ARC Team" button to notify the committee that new information is available for review.
          </p>
        </div>
      )}
    </div>
  );
};

// Additional Documents Section Component
const AdditionalDocumentsSection = ({ submission, user, activeHoaId, isBoardMember, isCommitteeMember, onContentAdded }) => {
  const [documents, setDocuments] = useState(submission.additional_documents || []);
  const [uploading, setUploading] = useState(false);
  const [showRefreshNote, setShowRefreshNote] = useState(false);
  
  const isOriginator = submission.created_by === user?.id;
  const canUpload = isOriginator || isBoardMember || isCommitteeMember;
  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const currentHoaId = activeHoaId || user?.hoa_id;
    let uploadedAny = false;
    
    try {
      for (const file of files) {
        // First upload the file
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadResponse = await api.post('/api/settings/upload-file', formData, {
          params: { hoa_id: currentHoaId },
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (uploadResponse.data.success) {
          // Then add document to submission
          const docResponse = await api.post(`/api/forms/submissions/${submission.id}/documents`, {
            file_id: uploadResponse.data.file_id,
            file_name: uploadResponse.data.filename,
            file_url: `/api/documents/${uploadResponse.data.file_id}/view`
          }, {
            params: { hoa_id: currentHoaId }
          });
          
          if (docResponse.data.success) {
            setDocuments([...documents, docResponse.data.document]);
            uploadedAny = true;
          }
        }
      }
      
      // Show refresh note for originator after uploading
      if (uploadedAny && isOriginator) {
        setShowRefreshNote(true);
        if (onContentAdded) onContentAdded();
      }
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };
  
  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="font-medium mb-3">📎 Additional Documents</h3>
      
      {/* Existing Documents */}
      <div className="space-y-2 mb-4">
        {documents.length === 0 ? (
          <p className="text-sm text-gray-500">No additional documents uploaded.</p>
        ) : (
          documents.map((doc, index) => {
            // Normalize document URL - handle both old format and new format
            let docUrl = doc.file_url;
            if (doc.file_id && (!docUrl || docUrl.includes('/api/forms/files/'))) {
              // Fix incorrect URL format to use the correct document endpoint
              docUrl = `/api/documents/${doc.file_id}/view`;
            }
            
            return (
              <div key={doc.id || index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center gap-2">
                  <span className="text-lg">📄</span>
                  <div>
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${docUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {doc.file_name}
                    </a>
                    <p className="text-xs text-gray-500">
                      Uploaded by {doc.uploaded_by_name} on {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Upload New Document */}
      {canUpload && (
        <div>
          <label className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 cursor-pointer text-sm">
            {uploading ? 'Uploading...' : '➕ Upload Document'}
            <input
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt"
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </label>
          <p className="text-xs text-gray-500 mt-1">PDF, Images, Word documents, Text files. Max 10MB.</p>
        </div>
      )}
      
      {/* Refresh note for originator after uploading */}
      {showRefreshNote && isOriginator && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            ✓ Document uploaded successfully. Refresh the page to see updates and use the "Alert ARC Team" button to notify the committee that new information is available for review.
          </p>
        </div>
      )}
    </div>
  );
};

// Alert ARC Team Section Component - shows only for originators when they have pending updates
const AlertArcTeamSection = ({ submission, user, activeHoaId }) => {
  const [loading, setLoading] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  
  // Check if there's a pending alert from the database
  const hasPendingAlert = submission.has_pending_alert === true;
  
  const handleAlertArcTeam = async () => {
    if (!hasPendingAlert || loading) return;
    
    setLoading(true);
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      const response = await api.post(`/api/forms/submissions/${submission.id}/alert-arc-team`, {}, {
        params: { hoa_id: currentHoaId }
      });
      
      if (response.data.success) {
        setAlertSent(true);
      } else {
        alert('Failed to send alert: ' + (response.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to alert ARC team:', error);
      alert('Failed to alert ARC team. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Don't show if alert was just sent
  if (alertSent) {
    return (
      <div className="mt-6 border-t pt-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-700">
            <span className="text-lg">✅</span>
            <span className="font-medium">Alert sent successfully!</span>
          </div>
          <p className="text-sm text-green-600 mt-1">The ARC team has been notified about your updates.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="font-medium mb-3">🔔 Notify ARC Team</h3>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700 mb-3">
          {hasPendingAlert 
            ? "You've added new information to this request. Click below to notify the ARC team that your updates are ready for review."
            : "Add a comment or upload a document above, then you can alert the ARC team that new information is available."}
        </p>
        <button
          onClick={handleAlertArcTeam}
          disabled={!hasPendingAlert || loading}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            hasPendingAlert 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          } disabled:opacity-50`}
        >
          {loading ? 'Sending...' : '🔔 Alert ARC Team'}
        </button>
      </div>
    </div>
  );
};

// Submission Detail Modal
const SubmissionDetailModal = ({ submission, onClose, onUpdateStatus, isBoardMember, hoaTrackSettings, subscriptionTier }) => {
  const { user } = useContext(AuthContext);
  const { activeHoaId } = useMultiHOA();
  
  // Local state - FIXED: Initialize localCommittees first
  const [localCommittees, setLocalCommittees] = useState([]);
  // Check if user is a team member for this assignment
  const [userTeamMemberships, setUserTeamMemberships] = useState({
    maintenance: false,
    security: false
  });
  // Check if user is a committee member for this request
  const [isCommitteeMember, setIsCommitteeMember] = useState(false);

  useEffect(() => {
    const maintenanceTeam = localCommittees.find(c => c?.name?.toLowerCase?.().includes('maintenance') || c?.type === 'maintenance');
    const securityTeam = localCommittees.find(c => c?.name?.toLowerCase?.().includes('security') || c?.type === 'security');

    const isMaintenanceMember = !!(maintenanceTeam?.members?.some?.(member => member?.email === user?.email || member?.id === user?.id) || user?.role === 'maintenance_team');
    const isSecurityMember = !!(securityTeam?.members?.some?.(member => member?.email === user?.email || member?.id === user?.id) || user?.role === 'security_team');

    setUserTeamMemberships({
      maintenance: isMaintenanceMember,
      security: isSecurityMember
    });
    
    // Check if user is a member of the committee this request is assigned to
    if (submission.assigned_to === 'committee' && submission.assigned_to_details) {
      const assignedCommittee = localCommittees.find(c => c.name === submission.assigned_to_details);
      if (assignedCommittee) {
        const isMember = assignedCommittee.members?.some?.(member => 
          member?.email === user?.email || member?.user_id === user?.id
        );
        setIsCommitteeMember(!!isMember);
      }
    }
  }, [localCommittees, user, submission]);

  const canAssignToSelf = (
    (submission.assigned_to === 'maintenance' && userTeamMemberships.maintenance) ||
    (submission.assigned_to === 'security' && userTeamMemberships.security)
  ) && !submission.assigned_to_details;
  const [status, setStatus] = useState(submission.status);
  const [note, setNote] = useState('');
  const [assignedTo, setAssignedTo] = useState(submission.assigned_to || 'board');
  const [assignedToDetails, setAssignedToDetails] = useState(submission.assigned_to_details || '');
  const [loading, setLoading] = useState(false);
  
  // Local submission state for optimistic UI updates (e.g., has_pending_alert)
  const [localSubmission, setLocalSubmission] = useState(submission);
  
  // Handler called when originator adds new content (comment or document)
  const handleContentAdded = () => {
    // Optimistically update the local state to enable the alert button
    setLocalSubmission(prev => ({
      ...prev,
      has_pending_alert: true
    }));
  };

  // Handle assignment change and clear assignment details when changing teams
  const handleAssignmentChange = (newAssignment) => {
    setAssignedTo(newAssignment);
    // Clear assignment details when switching unless it's committee (keep committee selection)
    if (newAssignment === 'board' || (newAssignment !== 'committee' && assignedTo === 'committee')) {
      setAssignedToDetails('');
    }
  };

  useEffect(() => {
    // Load committees when modal opens (needed for both board members and committee members)
    loadCommittees();
  }, []);

  const loadCommittees = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      // Use the same API endpoint as Communications tab
      const { data } = await api.get(`/api/hoa/${currentHoaId}/committees`);
      setLocalCommittees(Array.isArray(data.committees) ? data.committees : []);
    } catch (error) {
      console.error('Failed to load committees:', error);
      setLocalCommittees([]);
    }
  };

  const handleStatusUpdate = async () => {
    if (!isBoardMember && !isCommitteeMember) return;
    
    setLoading(true);
    try {
      await onUpdateStatus(submission.id, {
        status,
        note,
        assigned_to: assignedTo,
        assigned_to_details: assignedToDetails
      });
      onClose();
    } catch (error) {
      console.error('Status update failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">{submission.template_name}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>
          
          {/* HOA Track Progress */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Request Status</h3>
            <HOATrack status={submission.status} />
          </div>
          
          {/* Submission Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium mb-3">Request Details</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Submitted:</strong> {new Date(submission.created_at).toLocaleDateString()}</div>
                {submission.submitter_name && (
                  <div><strong>Submitted by:</strong> {submission.submitter_name}</div>
                )}
                {submission.submitter_email && (
                  <div><strong>Email:</strong> <a href={`mailto:${submission.submitter_email}`} className="text-blue-600 hover:underline">{submission.submitter_email}</a></div>
                )}
                {submission.submitter_property_address && (
                  <div><strong>Property:</strong> {submission.submitter_property_address}</div>
                )}
                {submission.answers?.after_the_fact && (
                  <div className="bg-amber-50 text-amber-800 px-2 py-1 rounded text-xs font-medium inline-block">
                    After the Fact Request
                  </div>
                )}
                <div><strong>Status:</strong> <span className="capitalize">{submission.status.replace('_', ' ')}</span></div>
                <div className="flex items-center justify-between">
                  <div>
                    <strong>Assigned to:</strong> <span className="capitalize">{submission.assigned_to.replace('_', ' ')}</span>
                    {submission.assigned_to_details && ` (${submission.assigned_to_details})`}
                    {!submission.assigned_to_details && (submission.assigned_to === 'maintenance' || submission.assigned_to === 'security') && (
                      <span className="text-gray-500 text-sm ml-2">- Unassigned</span>
                    )}
                  </div>
                  {canAssignToSelf && (
                    <button
                      onClick={async () => {
                        try {
                          await onUpdateStatus(submission.id, {
                            assigned_to_details: `${user.first_name || user.name || user.email} (${submission.assigned_to} team)`
                          });
                          // Update local state
                          setAssignedToDetails(`${user.first_name || user.name || user.email} (${submission.assigned_to} team)`);
                        } catch (error) {
                          console.error('Failed to assign to self:', error);
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                    >
                      🙋‍♂️ Assign to me
                    </button>
                  )}
                </div>
              </div>
              
              <h4 className="font-medium mt-4 mb-2">Answers</h4>
              <div className="space-y-2 text-sm bg-gray-50 p-3 rounded">
                {Object.entries(submission.answers).map(([key, value]) => {
                  // Handle attachments specially
                  if (key === 'attachments' && Array.isArray(value) && value.length > 0) {
                    return (
                      <div key={key}>
                        <strong className="capitalize">Attachments:</strong>
                        <div className="mt-1 space-y-1">
                          {value.map((att, idx) => {
                            if (att && att.file_url) {
                              return (
                                <div key={idx}>
                                  <a 
                                    href={`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    📎 {att.file_name || att.filename || 'Attachment'}
                                  </a>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    );
                  }
                  // Skip empty or non-renderable values
                  if (value === null || value === undefined || value === '') {
                    return null;
                  }
                  // Render normal values
                  return (
                    <div key={key}>
                      <strong className="capitalize">{key.replace('_', ' ')}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div>
              <h3 className="font-medium mb-3">Timeline</h3>
              <div className="space-y-2 text-sm">
                {submission.timeline?.map((entry, idx) => (
                  <div key={idx} className="border-l-2 border-gray-200 pl-3">
                    <div className="font-medium capitalize">{entry.status.replace('_', ' ')}</div>
                    <div className="text-gray-600">{new Date(entry.at).toLocaleString()}</div>
                    {entry.note && <div className="text-gray-700 mt-1">{entry.note}</div>}
                  </div>
                ))}
              </div>
              
              {/* Board Member & Committee Member Controls */}
              {(isBoardMember || isCommitteeMember) && (
                <div className="mt-6 p-4 border rounded">
                  <h4 className="font-medium mb-3">Update Status {isCommitteeMember && !isBoardMember && <span className="text-sm text-gray-600">(Committee Member)</span>}</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Status</label>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="new">New</option>
                        <option value="meeting_scheduled">Meeting Scheduled</option>
                        <option value="in_review">In Review</option>
                        <option value="need_info">Need Info</option>
                        <option value="conditionally_approved">Conditionally Approved</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Assign To</label>
                      <select
                        value={assignedTo}
                        onChange={(e) => handleAssignmentChange(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="board">Board</option>
                        {hoaTrackSettings?.maintenance_enabled !== false && subscriptionTier === 'premium' && (
                          <option value="maintenance">Maintenance</option>
                        )}
                        {hoaTrackSettings?.security_enabled === true && subscriptionTier === 'premium' && (
                          <option value="security">Security</option>
                        )}
                        {hoaTrackSettings?.committees_enabled !== false && (
                          <option value="committee">Committee</option>
                        )}
                      </select>
                    </div>
                    
                    {assignedTo === 'committee' && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Select Committee</label>
                        <select
                          value={assignedToDetails}
                          onChange={(e) => setAssignedToDetails(e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="">Select Committee</option>
                          {localCommittees.map(committee => (
                            <option key={committee.id} value={committee.name}>
                              {committee.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {(assignedTo === 'maintenance' || assignedTo === 'security') && (
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Assigned Team Member
                        </label>
                        <input
                          type="text"
                          value={assignedToDetails}
                          onChange={(e) => setAssignedToDetails(e.target.value)}
                          placeholder={`Leave blank until ${assignedTo} team member picks up this request`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          readOnly={!isBoardMember}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This will be automatically filled when a {assignedTo} team member assigns the task to themselves
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Note</label>
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="Add a note about this status change..."
                      />
                    </div>
                    
                    <button
                      onClick={handleStatusUpdate}
                      disabled={loading}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Updating...' : 'Update Status'}
                    </button>
                    
                    {/* Archive Button */}
                    {(status === 'approved' || status === 'rejected') && (
                      <button
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to archive this request? This will close it and move it to the archive.')) {
                            try {
                              const currentHoaId = activeHoaId || user?.hoa_id;
                              await api.post(`/api/forms/submissions/${submission.id}/archive`, {}, {
                                params: { hoa_id: currentHoaId }
                              });
                              alert('Request archived successfully');
                              onClose();
                              // Refresh submissions list
                              window.location.reload();
                            } catch (error) {
                              console.error('Archive failed:', error);
                              alert('Failed to archive request');
                            }
                          }
                        }}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 mt-2"
                      >
                        📦 Archive Request
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Comments Section */}
          <CommentsSection 
            submission={submission} 
            user={user} 
            activeHoaId={activeHoaId} 
            isBoardMember={isBoardMember}
            isCommitteeMember={isCommitteeMember}
            onContentAdded={handleContentAdded}
          />
          
          {/* Additional Documents Section */}
          <AdditionalDocumentsSection 
            submission={submission} 
            user={user} 
            activeHoaId={activeHoaId}
            isBoardMember={isBoardMember}
            isCommitteeMember={isCommitteeMember}
            onContentAdded={handleContentAdded}
          />
          
          {/* Alert ARC Team Section - Only for originators of ARC requests */}
          {localSubmission.template_key === 'arc' && localSubmission.created_by === user?.id && (
            <AlertArcTeamSection
              submission={localSubmission}
              user={user}
              activeHoaId={activeHoaId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default function FormsTab({ selectedSubmissionId, subscriptionTier = 'basic', hoaSettings = null }) {
  const { user } = useContext(AuthContext);
  const { activeHoaId } = useMultiHOA();
  const [activeSubTab, setActiveSubTab] = useState('forms');
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBookingCalendar, setShowBookingCalendar] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState('');
  
  // Helper function to check if user has a specific add-on
  const hasAddon = (addonId) => {
    if (!hoaSettings?.subscription) return false;
    
    // Check in addons array
    if (Array.isArray(hoaSettings.subscription.addons) && 
        hoaSettings.subscription.addons.includes(addonId)) {
      return true;
    }
    
    // Check in plan.add_ons array (legacy format)
    if (hoaSettings.subscription.plan?.add_ons && Array.isArray(hoaSettings.subscription.plan.add_ons)) {
      return hoaSettings.subscription.plan.add_ons.some(addon => 
        addon.addon_id === addonId || addon.id === addonId
      );
    }
    
    return false;
  };
  
  const [hoaTrackSettings, setHoaTrackSettings] = useState({
    committees_enabled: true,
    security_enabled: true,
    maintenance_enabled: true
  });
  
  // Track if amenity forms are enabled
  const [amenityFormsEnabled, setAmenityFormsEnabled] = useState(false);
  
  // Archive filters
  const [archiveFilterType, setArchiveFilterType] = useState('all');
  const [archiveFilterAddress, setArchiveFilterAddress] = useState('');
  const [archiveFilterName, setArchiveFilterName] = useState('');

  const isBoardMember = user?.role && ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(user.role);

  useEffect(() => {
    const currentHoaId = activeHoaId || user?.hoa_id;
    if (currentHoaId) {
      loadTemplates();
      loadSubmissions();
      loadHOATrackSettings();
    }
  }, [activeHoaId, user?.hoa_id, activeSubTab]);

  const loadHOATrackSettings = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      const response = await api.get(`/api/hoa-track/settings/${currentHoaId}`);
      if (response.data.success) {
        setHoaTrackSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to load HOA Track settings:', error);
    }
  };

  // Listen for switchToTab events from Contact Board button
  useEffect(() => {
    const handleSwitchToTab = (event) => {
      const { formType } = event.detail;
      if (formType === 'general_request') {
        // Find and select the Contact Board template
        const generalTemplate = templates.find(t => 
          t.key === 'general_request' || 
          t.name?.toLowerCase().includes('contact board') ||
          t.name?.toLowerCase().includes('general') ||
          t.name?.toLowerCase().includes('board request')
        );
        if (generalTemplate) {
          setSelectedTemplate(generalTemplate);
        }
      }
    };

    window.addEventListener('switchToTab', handleSwitchToTab);
    return () => {
      window.removeEventListener('switchToTab', handleSwitchToTab);
    };
  }, [templates]);

  // Handle navigation from widget
  useEffect(() => {
    if (selectedSubmissionId && submissions.length > 0) {
      const submission = submissions.find(s => s.id === selectedSubmissionId);
      if (submission) {
        setSelectedSubmission(submission);
        setActiveSubTab('tracker'); // Switch to tracker tab to show the submission
      }
    }
  }, [selectedSubmissionId, submissions]);

  const loadTemplates = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      // Get HOA settings to see which forms are enabled
      const settingsResponse = await api.get('/api/settings/hoa-track', {
        params: { hoa_id: currentHoaId }
      });
      
      // CRITICAL: Check if enabled_forms exists in settings
      // If enabled_forms is explicitly an array (even if empty), respect it
      // If it's undefined/null, use default behavior
      const settings = settingsResponse.data.settings || {};
      const hasEnabledFormsConfig = Array.isArray(settings.enabled_forms);
      const enabledForms = hasEnabledFormsConfig ? settings.enabled_forms : [
        'arc', 'maintenance', 'violation_appeal', 'pet_registration', 
        'vehicle_registration', 'amenity_reservation', 'general_request'
      ];
      
      console.log(`[HOA Track] enabled_forms configured: ${hasEnabledFormsConfig}, count: ${enabledForms.length}`);
      
      // Check if amenity forms are enabled (for tab visibility)
      setAmenityFormsEnabled(enabledForms.includes('amenity_reservation'));
      
      // Get all templates
      const templatesResponse = await api.get('/api/forms/templates', {
        params: { hoa_id: currentHoaId }
      });
      
      
      // Filter templates based on HOA Track settings (enabled_forms) - single source of truth
      const allTemplates = templatesResponse.data.templates || [];
      const filteredTemplates = allTemplates.filter(template => {
        // First check if form is enabled in settings
        if (!enabledForms.includes(template.key)) {
          console.log(`[HOA Track] Form "${template.key}" hidden: not in enabled_forms`);
          return false;
        }
        return true;
      });
      
      console.log(`[HOA Track] Showing ${filteredTemplates.length} forms out of ${allTemplates.length} total`);
      setTemplates(filteredTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
      // Fallback - load all templates if settings fail
      try {
        const currentHoaId = activeHoaId || user?.hoa_id;
        const response = await api.get('/api/forms/templates', {
          params: { hoa_id: currentHoaId }
        });
        setTemplates(response.data.templates || []);
      } catch (fallbackError) {
        console.error('Fallback template loading failed:', fallbackError);
      }
    }
  };

  const loadSubmissions = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const params = { hoa_id: currentHoaId };
      
      // Filter submissions based on active tab
      if (activeSubTab === 'archive') {
        params.status = 'closed';
      } else if (activeSubTab === 'tracker') {
        // Don't filter by status for tracker - show all active submissions
      }
      
      const response = await api.get('/api/forms/submissions', { params });
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    }
  };

  // Auto-assignment logic based on form type
  const getAutoAssignment = (templateKey, answers) => {
    // Maintenance requests auto-assign to maintenance team
    if ((templateKey === 'maintenance_request' || 
         templateKey?.includes('maintenance') ||
         answers?.request_type?.toLowerCase().includes('maintenance')) &&
        hoaTrackSettings?.maintenance_enabled !== false) {
      return {
        assigned_to: 'maintenance',
        assigned_to_details: 'Auto-assigned to maintenance team'
      };
    }
    
    // Security requests auto-assign to security team
    if ((templateKey === 'security_request' || 
         templateKey?.includes('security') ||
         answers?.request_type?.toLowerCase().includes('security')) &&
        hoaTrackSettings?.security_enabled === true) {
      return {
        assigned_to: 'security',
        assigned_to_details: 'Auto-assigned to security team'
      };
    }
    
    // Default to board for all other requests
    return {
      assigned_to: 'board',
      assigned_to_details: 'Requires board review'
    };
  };

  const handleSubmitForm = async (templateKey, answers) => {
    setLoading(true);
    try {
      // Get auto-assignment based on form type
      const autoAssignment = getAutoAssignment(templateKey, answers);
      
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      await api.post('/api/forms/submit', {
        template_key: templateKey,
        answers,
        ...autoAssignment
      }, {
        params: { hoa_id: currentHoaId }
      });
      
      await loadSubmissions();
      alert('Form submitted successfully!');
    } catch (error) {
      console.error('Form submission failed:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (submissionId, updateData) => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      await api.post(`/api/forms/submissions/${submissionId}/status`, updateData, {
        params: { hoa_id: currentHoaId }
      });
      
      await loadSubmissions();
    } catch (error) {
      console.error('Status update failed:', error);
      throw error;
    }
  };

  const handleBookingSelection = (bookingData) => {
    // Handle booking selection logic here
    console.log('Booking selected:', bookingData);
    setShowBookingCalendar(false);
  };

  const renderFormsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Submit a Request</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <button
              key={template.key}
              onClick={() => setSelectedTemplate(template)}
              className="p-4 text-left rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="font-medium text-gray-900">{template.name}</div>
              <div className="text-sm text-gray-500 mt-1">
                {template.fields?.length || 0} questions
              </div>
              {template.description && (
                <div className="text-xs text-gray-400 mt-2 line-clamp-2">
                  {template.description}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Your Recent Requests</h3>
        <div className="space-y-3">
          {submissions.slice(0, 5).map(submission => (
            <div
              key={submission.id}
              onClick={() => setSelectedSubmission(submission)}
              className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 cursor-pointer transition-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{submission.template_name}</div>
                  <div className="text-sm text-gray-500">
                    Submitted {new Date(submission.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded capitalize ${
                  submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                  submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  submission.status === 'in_review' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {submission.status.replace('_', ' ')}
                </span>
              </div>
              <div className="mt-3">
                <HOATrack status={submission.status} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTrackerTab = () => {
    const activeSubmissions = submissions.filter(s => s.status !== 'closed');
    
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {isBoardMember ? 'All Active Requests' : 'Your Active Requests'} ({activeSubmissions.length})
        </h3>
        
        <div className="space-y-3">
          {activeSubmissions.map(submission => (
            <div
              key={submission.id}
              onClick={() => setSelectedSubmission(submission)}
              className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 cursor-pointer transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-medium">{submission.template_name}</div>
                  <div className="text-sm text-gray-500">
                    {isBoardMember && submission.submitter_name && (
                      <>By {submission.submitter_name} • </>
                    )}
                    {new Date(submission.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Assigned to: <span className="capitalize">{submission.assigned_to.replace('_', ' ')}</span>
                    {submission.assigned_to_details && ` (${submission.assigned_to_details})`}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded capitalize whitespace-nowrap ${
                  submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                  submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  submission.status === 'in_review' ? 'bg-blue-100 text-blue-800' :
                  submission.status === 'need_info' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {submission.status.replace('_', ' ')}
                </span>
              </div>
              <div className="mt-3">
                <HOATrack status={submission.status} />
              </div>
            </div>
          ))}
        </div>
        
        {activeSubmissions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No active requests found.
          </div>
        )}
      </div>
    );
  };

  const renderArchiveTab = () => {
    let archivedSubmissions = submissions.filter(s => s.status === 'closed' || s.status === 'approved' || s.status === 'rejected');
    
    // Apply filters
    if (archiveFilterType !== 'all') {
      archivedSubmissions = archivedSubmissions.filter(s => s.template_key === archiveFilterType || s.template_name === archiveFilterType);
    }
    if (archiveFilterAddress) {
      archivedSubmissions = archivedSubmissions.filter(s => 
        s.answers?.address?.toLowerCase().includes(archiveFilterAddress.toLowerCase()) ||
        s.answers?.property_address?.toLowerCase().includes(archiveFilterAddress.toLowerCase())
      );
    }
    if (archiveFilterName) {
      archivedSubmissions = archivedSubmissions.filter(s =>
        s.submitter_name?.toLowerCase().includes(archiveFilterName.toLowerCase())
      );
    }
    
    // Export function
    const exportArchive = () => {
      if (archivedSubmissions.length === 0) {
        alert('No archived requests to export');
        return;
      }
      
      // Create CSV content
      let csv = 'Type,Submitted By,Address,Status,Completed Date\n';
      archivedSubmissions.forEach(s => {
        const address = s.answers?.address || s.answers?.property_address || 'N/A';
        const completedDate = new Date(s.updated_at).toLocaleDateString();
        csv += `"${s.template_name}","${s.submitter_name || 'N/A'}","${address}","${s.status}","${completedDate}"\n`;
      });
      
      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hoa-track-archive-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    };
    
    // Get unique request types for filter
    const requestTypes = [...new Set(submissions.filter(s => s.status === 'closed' || s.status === 'approved' || s.status === 'rejected').map(s => s.template_name))];
    
    return (
      <div className="space-y-4">
        {/* Header with Export */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Archive ({archivedSubmissions.length})
          </h3>
          <button
            onClick={exportArchive}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
          >
            📊 Export Archive
          </button>
        </div>
        
        {/* Filters */}
        {isBoardMember && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-gray-50 p-4 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Type</label>
              <select
                value={archiveFilterType}
                onChange={(e) => setArchiveFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Types</option>
                {requestTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Address</label>
              <input
                type="text"
                value={archiveFilterAddress}
                onChange={(e) => setArchiveFilterAddress(e.target.value)}
                placeholder="Enter address..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Name</label>
              <input
                type="text"
                value={archiveFilterName}
                onChange={(e) => setArchiveFilterName(e.target.value)}
                placeholder="Enter name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
        )}
        
        {/* Homeowner-only filters */}
        {!isBoardMember && (
          <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
            📋 Viewing your archived requests only
          </div>
        )}
        
        <div className="space-y-3">
          {archivedSubmissions.map(submission => (
            <div
              key={submission.id}
              onClick={() => setSelectedSubmission(submission)}
              className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 cursor-pointer transition-all opacity-75"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium">{submission.template_name}</div>
                  <div className="text-sm text-gray-500">
                    {isBoardMember && submission.submitter_name && (
                      <>By {submission.submitter_name} • </>
                    )}
                    {submission.answers?.address || submission.answers?.property_address ? (
                      <>{submission.answers?.address || submission.answers?.property_address} • </>
                    ) : null}
                    Completed {new Date(submission.updated_at).toLocaleDateString()}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                  submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {submission.status === 'approved' ? 'Approved' :
                   submission.status === 'rejected' ? 'Rejected' : 'Closed'}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {archivedSubmissions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No archived requests found.
          </div>
        )}
      </div>
    );
  };

  const renderAmenitiesTab = () => {
    const amenitySubmissions = submissions.filter(s => s.template_key === 'amenity_reservation');
    
    const statusColors = {
      'new': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            🏊‍♂️ Amenity Reservations ({amenitySubmissions.length})
          </h3>
          <button
            onClick={() => {
              const amenityTemplate = templates.find(t => t.key === 'amenity_reservation');
              if (amenityTemplate) {
                setSelectedTemplate(amenityTemplate);
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            + New Amenity Reservation
          </button>
        </div>
        
        <div className="space-y-3">
          {amenitySubmissions.map(submission => (
            <div
              key={submission.id}
              onClick={() => setSelectedSubmission(submission)}
              className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 cursor-pointer transition-all"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {submission.answers?.amenity_type || 'Amenity Reservation'}
                    </h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[submission.status] || 'bg-gray-100 text-gray-800'}`}>
                      {submission.status}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex items-center gap-4">
                      <span>📅 {submission.answers?.date || 'No date specified'}</span>
                      <span>🕐 {submission.answers?.start_time || 'No time'} - {submission.answers?.end_time || 'No time'}</span>
                    </div>
                    
                    {submission.answers?.guest_count && (
                      <div>👥 {submission.answers.guest_count} guests</div>
                    )}
                    
                    {submission.answers?.event_type && (
                      <div>🎯 {submission.answers.event_type}</div>
                    )}
                    
                    <div className="text-xs text-gray-500 mt-2">
                      Submitted: {new Date(submission.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-xs text-gray-500">#{submission.id.slice(-6)}</div>
                  {submission.status === 'approved' && (
                    <div className="text-green-600 text-sm font-medium mt-1">✅ Confirmed</div>
                  )}
                  {submission.status === 'pending' && (
                    <div className="text-yellow-600 text-sm font-medium mt-1">⏳ Pending</div>
                  )}
                  {submission.status === 'rejected' && (
                    <div className="text-red-600 text-sm font-medium mt-1">❌ Rejected</div>
                  )}
                </div>
              </div>
              
              {submission.answers?.special_requests && (
                <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  <strong>Special Requests:</strong> {submission.answers.special_requests}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {amenitySubmissions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">🏊‍♂️</div>
            <p>No amenity reservations found</p>
            <p className="text-sm mt-1">Create your first amenity reservation to get started</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Sub-navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {['forms', 'tracker', 'amenities', 'archive']
            .filter(tab => {
              // Hide amenities tab if amenity forms are not enabled
              if (tab === 'amenities' && !amenityFormsEnabled) {
                return false;
              }
              return true;
            })
            .map(tab => (
            <button
              key={tab}
              onClick={() => setActiveSubTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeSubTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'forms' ? 'Forms' : 
               tab === 'tracker' ? 'HOA Tracker' : 
               tab === 'amenities' ? 'Amenities Track' : 'Archive'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeSubTab === 'forms' && renderFormsTab()}
      {activeSubTab === 'tracker' && renderTrackerTab()}
      {activeSubTab === 'amenities' && renderAmenitiesTab()}
      {activeSubTab === 'archive' && renderArchiveTab()}

      {/* Modals */}
      {selectedTemplate && (
        <FormModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onSubmit={handleSubmitForm}
        />
      )}

      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onUpdateStatus={handleUpdateStatus}
          isBoardMember={isBoardMember}
          hoaTrackSettings={hoaTrackSettings}
          subscriptionTier={subscriptionTier}
        />
      )}
      
      {/* Booking Calendar Modal */}
      {showBookingCalendar && (
        <AmenityBookingCalendar
          amenityType={selectedAmenity}
          amenitySettings={{
            hours_start: "07:00",
            hours_end: "22:00", 
            max_capacity: selectedAmenity === 'Tennis Court' ? 2 : 1,
            block_minutes: 60
          }}
          onBook={handleBookingSelection}
          onClose={() => setShowBookingCalendar(false)}
        />
      )}
    </div>
  );
}